import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import {
	CommonSwitch,
	CustomDropdown,
	CustomTextInput,
	UserInfoTextInput,
	LabeledSwitch,
} from "../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import {
	CompanyIcon,
	DeleteIcon,
	EmailIcon,
	InvoiceIcon,
	LockIcon,
	UserIcon,
	WorldIcon,
} from "../../common-components/svgIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { FrontendProfileTab, Severity } from "node-library/dist/constants";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import {
	ArrowButton,
	BlueButton,
	OutlinedButton,
	TextButton,
} from "../../common-components/button";
import AddIcon from "@mui/icons-material/Add";

import CopyEmailSection from "../mainPages/common/copyEmailSection";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { sendGETRequest } from "../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import moment from "moment-timezone";
import TimeZoneDropdown from "../../common-components/timezoneDropdown";
import CountryProvinceSelector from "../../common-components/countryAndProvinceSelector";

interface Props {
	// States for user info
	clientInformationFields: Array<any>;
	companyInformationFields1: Array<any>;
	companyInformationFields2: Array<any>;

	country: string;
	setCountry: React.Dispatch<React.SetStateAction<string>>;
	province: string;
	setProvince: React.Dispatch<React.SetStateAction<string>>;

	enableNotificationEmail: boolean;
	setEnableNotificationEmail: React.Dispatch<React.SetStateAction<boolean>>;
	// States for test report emails
	testReportEmails: Array<string>;
	setTestReportEmails: React.Dispatch<React.SetStateAction<Array<string>>>;

	timezone: string;
	setTimezone: React.Dispatch<React.SetStateAction<string>>;

	// States for password modal
	setChangePasswordModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

	// States for error messages

	testReportEmailsErrorMsgs: Array<string>;
	setTestReportEmailsErrorMsgs: React.Dispatch<
		React.SetStateAction<Array<string>>
	>;

	// Props for unsaved changes state
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserClientInfoSection(props: Props) {
	const { t: translation } = useTranslation();

	const { openSnackbar } = useSnackbar();

	const {
		clientInformationFields,
		companyInformationFields1,
		companyInformationFields2,
		country,
		setCountry,
		province,
		setProvince,
		enableNotificationEmail,
		setEnableNotificationEmail,
		testReportEmails,
		setTestReportEmails,
		timezone,
		setTimezone,
		setChangePasswordModalOpen,

		testReportEmailsErrorMsgs,
		setTestReportEmailsErrorMsgs,
		setUnsavedChanges,
	} = props;

	function newTestReportEmail() {
		if (testReportEmails?.length >= 5) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("EmailLimitReachedDescription"),
				<YellowWarnIcon />
			);
			return;
		}
		setTestReportEmails((prevItems) => [...prevItems, ""]);
		setTestReportEmailsErrorMsgs((prevItems) => [...prevItems, ""]);
		setUnsavedChanges(true);
	}
	function removeTestReportEmail(index: number) {
		if (index >= 0 && index < testReportEmails.length) {
			setTestReportEmails((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setTestReportEmailsErrorMsgs((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setUnsavedChanges(true);
		} else {
			return;
		}
	}
	function updateTestReportEmail(index: number, newItem: string) {
		// Create a new array with the updated item at the specific index
		const updatedEmails = testReportEmails.map(
			(email, i) => (i === index ? newItem : email) // If the index matches, replace the item
		);

		// Update the state with the new array
		setTestReportEmails(updatedEmails);
		setUnsavedChanges(true);
	}

	const testReportEmailItems: Array<React.ReactNode> = testReportEmails.map(
		(email, index) => {
			return (
				<div
					key={index}
					style={{ display: "flex", gap: "10px", alignItems: "center" }}
				>
					<div style={{ width: "100%" }}>
						<CustomTextInput
							id={`test-email-input-${index}`}
							value={email}
							setValue={(value) => {
								setUnsavedChanges(true);
								updateTestReportEmail(index, value);
							}}
							placeholder={translation("EmailAddress")}
							required
							errorMsg={testReportEmailsErrorMsgs[index]}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = testReportEmailsErrorMsgs.map(
									(txt, i) => (i === index ? msg : txt) // If the index matches, replace the item
								);

								// Update the state with the new array
								setTestReportEmailsErrorMsgs(updatedMsgs);
							}}
						/>
					</div>
					<div
						id={`delete-test-email-input-${index}`}
						style={{ cursor: "pointer" }}
						onClick={() => {
							setUnsavedChanges(true);
							removeTestReportEmail(index);
						}}
					>
						<DeleteIcon></DeleteIcon>
					</div>
				</div>
			);
		}
	);

	const clientInformation: React.ReactNode = (
		<div>
			<div className="profile-information-container">
				<div className="profile-icon-header-row typography-heading-03">
					<UserIcon
						strokeColor={NeutralColor.NEUTRAL_GREY_900}
						fillColor={NeutralColor.NEUTRAL_GREY_900}
					></UserIcon>
					{translation("ClientInformation")}
				</div>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("ClientInformationDescription")}
				</div>
				<div className="profile-client-info-input-row">
					{clientInformationFields.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>

			<div className="profile-information-container">
				<div className="profile-icon-header-row typography-heading-03">
					<CompanyIcon strokeColor="white"></CompanyIcon>
					{translation("CompanyInformation")}
				</div>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("CompanyInformationDescription")}
				</div>

				<div className="profile-company-info-input-row">
					{companyInformationFields1.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div className="profile-client-info-input-row">
					{companyInformationFields2.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
					<CountryProvinceSelector
						countryDropdownId={"profile-country-input"}
						provinceDropdownId={"profile-province-input"}
						selectedCountryName={country}
						setSelectedCountryName={setCountry}
						selectedState={province}
						setSelectedState={setProvince}
						setUnsavedChanges={setUnsavedChanges}
					></CountryProvinceSelector>
				</div>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>
			<div className="profile-page-copy-email-container">
				<CopyEmailSection
					title={translation("DefaultSendTestReportCopiesTo")}
					// customHeaderClass="profile-default-email-header"
					button={
						<TextButton
							id={"add-test-report-email-button"}
							onClick={() => {
								newTestReportEmail();
							}}
							size="xsmall"
						>
							<AddIcon style={{ fontSize: 20 }} />
							<span className="typography-body-02">{translation("Email")}</span>
						</TextButton>
					}
					items={testReportEmailItems}
				></CopyEmailSection>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>
			<div className="common-card-header profile-card-header-wrap profile-information-container">
				<div>
					<div className="profile-icon-header-row typography-heading-03">
						<EmailIcon
							size={24}
							strokeColor="white"
							fillColor={NeutralColor.NEUTRAL_GREY_900}
						></EmailIcon>
						{translation("OrderStatusUpdateNotifications")}
					</div>
					<div
						className="typography-caption"
						style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					>
						{translation("OrderStatusUpdateNotificationsDescription")}
					</div>
				</div>
				<CommonSwitch
					id={"enable-email-notification-switch"}
					checked={enableNotificationEmail}
					setChecked={setEnableNotificationEmail}
				/>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>
			<div className="common-card-header profile-card-header-wrap profile-information-container">
				<div>
					<div className="profile-icon-header-row typography-heading-03">
						<WorldIcon></WorldIcon>
						{translation("TimeZone")}
					</div>
					<div
						className="typography-caption"
						style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					>
						{translation("TimeZoneSettingDescription")}
					</div>
				</div>
				<TimeZoneDropdown
					value={timezone}
					setValue={setTimezone}
				></TimeZoneDropdown>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>

			<div className="profile-password-header">
				<div className="profile-icon-header-row typography-heading-03">
					<LockIcon
						strokeColor={NeutralColor.NEUTRAL_GREY_900}
						fillColor={NeutralColor.NEUTRAL_GREY_900}
					></LockIcon>
					{translation("Password")}
				</div>
				<OutlinedButton
					id="change-password-button"
					size="medium"
					children={translation("ChangePassword")}
					//onClick={() => setCurrentPageIndex(1)}
					onClick={() => {
						setChangePasswordModalOpen(true);
					}}
					outlineColor={NeutralColor.NEUTRAL_GREY_900}
				></OutlinedButton>
			</div>
		</div>
	);

	return clientInformation;
}
